import { useState, useRef, MutableRefObject, useEffect } from 'react';
import { GenericLink, FAIcon, useNextgenRouter } from '@nextgen-web-framework/all';
import ShopByBrands from '../ShopByBrands/ShopByBrands';
import CategoryItemContainer from '../navigationItem/NavigationMenuItem/CategoryItemContainer/CategoryItemContainer';
import ImagesAndLinksContainer from '../ImagesAndLinksContainer/ImagesAndLinksContainer';
import type { NavigationMenu, NavigationMenuProps } from './type/propsTypes';
import styles from './NavigationBar.module.scss';

const NavigationBar = ({ navigations, activeRoute }: NavigationMenuProps) => {
  const [activeTab, setActiveTab] = useState('');

  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;
  const menuRef = useRef() as MutableRefObject<HTMLDivElement>;
  const contentRef = useRef() as MutableRefObject<HTMLDivElement>;
  const outerMenuRef = useRef() as MutableRefObject<HTMLDivElement>;
  const query = useNextgenRouter()?.query;

  useEffect(() => {
    if (activeTab) {
      setActiveTab('');
    }
  }, [query]);

  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent | KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const clickedId = target?.getElementsByTagName('div')?.[0]?.id;
      if (
        (!menuRef.current?.contains(target) && clickedId === 'content-full-id') ||
        !outerMenuRef.current?.contains(target)
      ) {
        animateSlideUp();
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        checkIfClickedOutside(e);
      }
    });
    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
      document.removeEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          checkIfClickedOutside(e);
        }
      });
    };
  }, [menuRef, outerMenuRef]);
  useEffect(() => {
    const megaMenuModalElement = document.getElementsByClassName('amwayMegaMenuModal');
    if (megaMenuModalElement?.length > 0) {
      if (
        megaMenuModalElement[0].scrollHeight <=
        megaMenuModalElement[0].getElementsByClassName('full-content')[0].scrollHeight
      ) {
        document.documentElement.style.setProperty('--modalContentBottom', `${0}px`);
      } else {
        document.documentElement.style.setProperty('--modalContentBottom', `auto`);
      }
    }
  }, [activeTab]);

  const animateSlideUp = () => {
    modalRef.current?.classList.remove(`${styles['slide-down']}`);
    setTimeout(() => {
      setActiveTab('');
    }, 200);
  };

  const onCategoryCLick = (menuId: string) => {
    if (activeTab !== menuId) {
      if (activeTab !== '') {
        contentRef.current.children[0].classList.add(`${styles['fade-out']}`);
        setActiveTab(menuId);
        return;
      }
      setActiveTab(menuId);
    } else {
      animateSlideUp();
    }
  };
  const renderMegaMenu = (navItem: NavigationMenu) => {
    if (navItem.menuType === 'images_and_links') {
      return <ImagesAndLinksContainer categories={navItem.categories} />;
    } else if (navItem.menuType === 'links_only') {
      return (
        <>
          {navItem.categories.map((category) => (
            <CategoryItemContainer key={category.title} category={category} title={category.title} />
          ))}
        </>
      );
    } else if (navItem.menuType === 'images_only') {
      return <ShopByBrands categories={navItem.categories} />;
    }
    return <></>;
  };
  return (
    <div ref={outerMenuRef}>
      <nav data-testid="navbar-test-id">
        <ul className={styles['header-item-list']}>
          {navigations &&
            navigations.map((item) => {
              if (item.url || item.categories.length === 0) {
                return (
                  <div className={styles['link-menu-wrapper']} key={item.title}>
                    <GenericLink
                      href={item.url ?? ''}
                      className={` ${
                        item.url === activeRoute ? `${styles['active-link-label']}` : `${styles['link-menu-label']}`
                      } `}
                    >
                      {item.title}
                    </GenericLink>
                  </div>
                );
              }
              return (
                <div
                  tabIndex={0}
                  key={item.menuId}
                  role="button"
                  aria-label="toggle menu"
                  data-testid="megamenu-title-testId"
                  className={styles['megamenu-title-container']}
                  onClick={() => onCategoryCLick(item.menuId)}
                  onKeyDown={() => onCategoryCLick(item.menuId)}
                >
                  <span className={styles['megamenu-title']}>{item.title} </span>
                  <FAIcon
                    className={styles['megamenu-icon']}
                    iconName={activeTab === item.menuId ? 'chevron-up' : 'chevron-down'}
                    data-testid="navbar-icon-test-id"
                  />
                </div>
              );
            })}
        </ul>
      </nav>
      <div
        id="amway-header-modal"
        data-testid="megamenu-modal-id"
        className={`${styles['amway-header-megamenu-modal']} ${
          activeTab !== '' ? `${styles['activeMenu']} ${styles['slide-down']} amwayMegaMenuModal` : ''
        } `}
        ref={modalRef}
      >
        <>
          <div id="content-full-id" className={styles['ahm-content-full']} ref={menuRef}>
            <div className={styles['ahm-on-scroll']}>
              <div id="test_it" ref={contentRef} className={`${styles['ahm-list']} full-content`}>
                {navigations?.map((navItem) => activeTab === navItem.menuId && renderMegaMenu(navItem))}
              </div>
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

export default NavigationBar;
